/* ==========================================================================
   C2V : section d'accueil "Devis et personnalisation" (c2v-home-studio)
   - Charge les scripts du studio a l'approche de la section (ou au premier
     geste) : c2v-studio-core.js puis c2v-studio.js, et sa feuille de style.
   - Zone "Deposez votre logo" : le logo est traite comme dans le studio
     (fond retire) puis applique en direct sur chaque carte produit.
   - Clic sur une carte : ouvre le studio du produit, logo deja charge.
   - "Changer de produit" dans le studio : retour aux cartes.
   - Formulaire de devis (Formful) : couleur selon le produit choisi.
   - Avis de professionnels (sous les colonnes) : carrousel, points, "Lire la suite".
   - Textes : window.C2V_I18N (snippet c2v-i18n.liquid), sinon le francais du code.
   ========================================================================== */
(() => {
  'use strict';

  // Textes traduits (cles c2v.* des fichiers de langue), sinon le francais passe en argument. Variables {nom}.
  const T = (k, fr, vars) => {
    const d = window.C2V_I18N, v = d && d[k];
    // le filtre t de Shopify echappe le HTML des cles sans _html : on remet les caracteres d'origine
    const s = typeof v === 'string' && v && !/^translation missing/i.test(v)
      ? v.replace(/&(amp|lt|gt|quot|#39|#x27|#039);/g, (m, e) => ({ amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", '#x27': "'", '#039': "'" }[e]))
      : fr;
    return vars ? s.replace(/\{(\w+)\}/g, (m, n) => (Object.prototype.hasOwnProperty.call(vars, n) ? String(vars[n]) : m)) : s;
  };

  const loadJs = src => new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = src; s.async = true;
    s.onload = res; s.onerror = () => rej(new Error('script ' + src));
    document.head.appendChild(s);
  });
  const loadCss = href => new Promise(res => {
    if (!href || document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) { res(); return; }
    const l = document.createElement('link');
    l.rel = 'stylesheet'; l.href = href;
    l.onload = res; l.onerror = res;
    document.head.appendChild(l);
    setTimeout(res, 4000);
  });
  // petite pause entre deux rendus (sans requestAnimationFrame, qui s'arrete quand l'onglet est en arriere-plan)
  const nextFrame = () => new Promise(r => setTimeout(r, 16));
  const userErr = msg => Object.assign(new Error(msg), { user: true });

  let studioP = null;
  function ensureStudio(sec) {
    if (studioP) return studioP;
    const ds = sec.dataset;
    studioP = (async () => {
      await loadCss(ds.css);
      if (!window.C2VLib) await loadJs(ds.core);
      if (!window.C2VStudios) await loadJs(ds.ui);
      if (!window.C2VStudios) throw new Error('studio');
    })();
    studioP.catch(() => { studioP = null; });
    return studioP;
  }

  // Avis de professionnels sous les 2 colonnes : carrousel (fleches, points, "Lire la suite")
  function setupReviews(sec) {
    const box = sec.querySelector('[data-c2v-reviews]');
    const track = box && box.querySelector('[data-c2v-rv-track]');
    if (!track) return;
    const prev = box.querySelector('[data-c2v-rv-prev]'), next = box.querySelector('[data-c2v-rv-next]');
    const dotsEl = box.querySelector('[data-c2v-rv-dots]');
    const items = [...track.children];
    let pages = [0], dots = [], ticking = false;

    // debut de chaque groupe d'avis visibles ensemble
    const computePages = () => {
      const max = track.scrollWidth - track.clientWidth;
      if (items.length < 2 || max <= 2) return [0];
      const base = items[0].offsetLeft;
      const w = items[0].offsetWidth;
      const gap = items[1].offsetLeft - base - w;
      const per = Math.max(1, Math.floor((track.clientWidth + gap + 2) / (w + gap)));
      const out = [];
      for (let i = 0; i < items.length; i += per) {
        const pos = Math.min(items[i].offsetLeft - base, max);
        if (!out.length || pos - out[out.length - 1] > 2) out.push(pos);
      }
      if (max - out[out.length - 1] > 2) out.push(max);
      return out;
    };
    const current = () => {
      const x = track.scrollLeft, max = track.scrollWidth - track.clientWidth;
      if (x >= max - 4) return pages.length - 1;
      let k = 0;
      pages.forEach((p, i) => { if (p <= x + 4) k = i; });
      return k;
    };
    const go = i => {
      const k = Math.max(0, Math.min(pages.length - 1, i));
      track.scrollTo({ left: pages[k], behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };
    const update = () => {
      ticking = false;
      const x = track.scrollLeft, max = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = x <= 4;
      if (next) next.disabled = x >= max - 4;
      const k = current();
      dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === k);
        d.setAttribute('aria-current', i === k ? 'true' : 'false');
      });
    };
    const buildDots = () => {
      if (!dotsEl) return;
      dotsEl.textContent = '';
      dots = pages.length > 1 ? pages.map((p, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'c2v-home__rv-dot';
        b.setAttribute('aria-label', T('home.rv_page_aria', 'Avis, page {n} sur {total}', { n: i + 1, total: pages.length }));
        b.addEventListener('click', () => go(i));
        dotsEl.appendChild(b);
        return b;
      }) : [];
      dotsEl.hidden = dots.length < 2;
    };
    // "Lire la suite" seulement si le texte depasse les 6 lignes
    const checkMore = () => {
      track.querySelectorAll('.c2v-home__rv-card').forEach(card => {
        const txt = card.querySelector('[data-c2v-rv-text]'), more = card.querySelector('[data-c2v-rv-more]');
        if (!txt || !more || card.classList.contains('is-open')) return;
        more.hidden = txt.scrollHeight <= txt.clientHeight + 2;
      });
    };
    const layout = () => {
      pages = computePages();
      buildDots();
      checkMore();
      update();
    };

    track.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; setTimeout(update, 60); }
    }, { passive: true });
    if (prev) prev.addEventListener('click', () => go(current() - 1));
    if (next) next.addEventListener('click', () => go(current() + 1));
    track.addEventListener('click', e => {
      const more = e.target.closest('[data-c2v-rv-more]');
      if (!more) return;
      const card = more.closest('.c2v-home__rv-card');
      const open = !card.classList.contains('is-open');
      card.classList.toggle('is-open', open);
      more.setAttribute('aria-expanded', open ? 'true' : 'false');
      more.textContent = open ? T('home.rv_less', 'Réduire') : T('home.rv_more', 'Lire la suite');
      layout();
    });
    // editeur de theme : un avis selectionne passe a l'ecran
    track.addEventListener('shopify:block:select', e => {
      const li = e.target.closest && e.target.closest('.c2v-home__rv-item');
      if (li) track.scrollTo({ left: li.offsetLeft - items[0].offsetLeft, behavior: 'auto' });
    });

    if ('ResizeObserver' in window) new ResizeObserver(layout).observe(track);
    else window.addEventListener('resize', layout);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout);
    layout();
  }

  function setup(sec) {
    if (sec.dataset.c2vReady) return;
    sec.dataset.c2vReady = '1';
    setupReviews(sec);
    const $ = s => sec.querySelector(s);
    const input = $('[data-c2v-home-file]'), drop = $('[data-c2v-home-drop]');
    const fileCard = $('[data-c2v-home-filecard]'), msgEl = $('[data-c2v-home-msg]');
    const cards = [...sec.querySelectorAll('[data-c2v-card]')];
    const views = new Map();
    let homeFile = null, token = 0;

    // chargement des scripts : a l'approche de la section, ou au premier geste
    const warm = () => ensureStudio(sec).catch(() => {});
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(es => { if (es.some(e => e.isIntersecting)) { io.disconnect(); warm(); } }, { rootMargin: '600px 0px' });
      io.observe(sec);
    } else warm();
    ['pointerenter', 'focusin', 'touchstart', 'dragenter'].forEach(ev => sec.addEventListener(ev, warm, { once: true, passive: true }));

    const msg = (text, error) => {
      if (!msgEl) return;
      msgEl.hidden = !text;
      msgEl.textContent = text || '';
      msgEl.classList.toggle('is-error', !!error);
    };
    const configFor = key => {
      const el = [...sec.querySelectorAll('[data-c2v-studio][data-c2v-key]')].find(x => x.dataset.c2vKey === key);
      if (!el) return null;
      const read = sel => { try { return JSON.parse(el.querySelector(sel).textContent); } catch (e) { return null; } };
      return { model: read('[data-c2v-model]') || { kind: 'capote' }, images: read('[data-c2v-images]') || {} };
    };

    // Rendu du logo sur une carte, avec le meme moteur que le studio
    async function renderCard(card, lg, zone) {
      const L = window.C2VLib, key = card.dataset.c2vCard, cfg = configFor(key), cv = card.querySelector('.c2v-home__cv');
      if (!cfg || !cv) return;
      const m = cfg.model;
      if (m.kind === 'flat') {
        if (!(m.mm && m.mm[0] > 0 && m.mm[1] > 0)) return;
        let fv = views.get(key);
        if (!fv) { fv = new L.FlatView(await L.loadStudioImage('flat', cfg.images.flat), m); views.set(key, fv); }
        cv.width = fv.W; cv.height = fv.H;
        const art = m.art === 'visuel'
          ? { mode: 'visuel', canvas: L.logoOnWhite(lg.canvas, m.mm, 1200).canvas }
          : { mode: 'logo', canvas: lg.canvas };
        fv.render(L.ctx2d(cv), art, null);
      } else {
        if (!zone) return;
        let lid = views.get(key);
        if (!lid) { lid = new L.Lid3D(await L.loadStudioImage('3d', cfg.images['3d'])); views.set(key, lid); }
        cv.width = lid.W; cv.height = lid.H;
        lid.render(L.ctx2d(cv), [255, 255, 255], 0, zone, L.hexToRgb(L.INKS[1].hex));
      }
      card.classList.add('has-logo');
    }

    async function onFile(file) {
      const my = ++token;
      msg('');
      if (drop) drop.classList.add('is-busy');
      cards.forEach(c => c.classList.add('is-rendering'));
      try {
        await ensureStudio(sec);
        const L = window.C2VLib;
        if (file.size > L.MAX_FILE) throw userErr(T('common.file_too_big', 'Fichier trop lourd ({size} Mo). Maximum {max} Mo.', { size: (file.size / 1048576).toFixed(1).replace('.', L.DEC || ','), max: Math.round(L.MAX_FILE / 1048576) }));
        const type = L.fileKind(file);
        if (!type) throw userErr(T('common.format_unsupported', 'Format non pris en charge. Importez un PNG, JPG, SVG ou PDF.'));
        // copie en memoire : le fichier reste disponible pour le studio
        file = new File([await file.arrayBuffer()], file.name, { type: file.type, lastModified: file.lastModified });
        let src;
        if (type === 'raster') src = await L.rasterSource(file);
        else if (type === 'svg') src = await L.svgSource(file);
        else { const r = await L.renderPdf(file, 2000, 'rgba(0,0,0,0)'); src = { src: r.canvas, w: r.canvas.width, h: r.canvas.height }; }
        const lg = L.colorLogo(src.src, src.w, src.h);
        if (lg.empty) throw userErr(T('common.no_motif', "Aucun motif n'a été détecté dans ce fichier. Vérifiez que le logo n'est pas blanc sur fond blanc."));
        const an = L.analyzeLogo(src.src, src.w, src.h);
        const zone = an.empty ? null : L.buildZone(an);
        if (my !== token) return;
        homeFile = file;
        showFile(file, L.artThumb(lg.canvas, Math.min(2, Math.max(0.8, lg.bw / lg.bh)), '#F3F3F3'));
        for (const card of cards) {
          if (my !== token) return;
          try { await renderCard(card, lg, zone); } catch (e) { console.warn('[C2V accueil]', e); }
          card.classList.remove('is-rendering');
          await nextFrame();
        }
        if (lg.white) msg(T('home.warn_white', "Votre logo est blanc ou très clair : il sera peu visible sur des produits blancs. Dans le studio, vous pourrez choisir une couleur d'impression."), false);
      } catch (e) {
        if (my !== token) return;
        console.warn('[C2V accueil]', e);
        msg(e.user ? e.message : String(e && e.message) === 'pdfjs'
          ? T('common.pdf_failed', "Le lecteur PDF n'a pas pu se charger. Vérifiez votre connexion puis réessayez.")
          : T('common.read_failed', "Ce fichier n'a pas pu être lu. Essayez un autre format ({formats}).", { formats: T('common.formats_logo', 'PNG, SVG ou PDF') }), true);
      } finally {
        if (my === token) {
          if (drop) drop.classList.remove('is-busy');
          cards.forEach(c => c.classList.remove('is-rendering'));
        }
      }
    }

    function showFile(file, thumb) {
      if (!fileCard) return;
      fileCard.hidden = false;
      if (drop) drop.hidden = true;
      fileCard.querySelector('[data-c2v-home-name]').textContent = file.name;
      fileCard.querySelector('[data-c2v-home-thumb]').src = thumb.toDataURL('image/png');
    }
    function clearFile() {
      token++;
      homeFile = null;
      if (fileCard) fileCard.hidden = true;
      if (drop) { drop.hidden = false; drop.classList.remove('is-busy'); }
      cards.forEach(c => c.classList.remove('has-logo', 'is-rendering'));
      msg('');
    }

    if (input) {
      input.addEventListener('change', () => { const f = input.files[0]; input.value = ''; if (f) onFile(f); });
    }
    if (drop) {
      ['dragenter', 'dragover'].forEach(ev => drop.addEventListener(ev, e => { e.preventDefault(); drop.classList.add('is-drag'); }));
      ['dragleave', 'dragend'].forEach(ev => drop.addEventListener(ev, () => drop.classList.remove('is-drag')));
      drop.addEventListener('drop', e => {
        e.preventDefault(); drop.classList.remove('is-drag');
        const f = e.dataTransfer && e.dataTransfer.files[0];
        if (f) onFile(f);
      });
    }
    // depot du logo directement sur les cartes
    const grid = $('[data-c2v-home-cards]');
    if (grid && input) {
      ['dragenter', 'dragover'].forEach(ev => grid.addEventListener(ev, e => { if (e.dataTransfer && [...e.dataTransfer.types].includes('Files')) { e.preventDefault(); grid.classList.add('is-drag'); } }));
      ['dragleave', 'dragend'].forEach(ev => grid.addEventListener(ev, e => { if (!grid.contains(e.relatedTarget)) grid.classList.remove('is-drag'); }));
      grid.addEventListener('drop', e => {
        const f = e.dataTransfer && e.dataTransfer.files[0];
        grid.classList.remove('is-drag');
        if (!f) return;
        e.preventDefault(); onFile(f);
      });
    }
    const change = $('[data-c2v-home-change]'), remove = $('[data-c2v-home-remove]');
    if (change && input) change.addEventListener('click', () => input.click());
    if (remove) remove.addEventListener('click', clearFile);

    // ouverture du studio du produit
    sec.addEventListener('click', async e => {
      const card = e.target.closest('[data-c2v-card]');
      if (!card || !sec.contains(card)) return;
      e.preventDefault();
      if (card.classList.contains('is-opening')) return;
      card.classList.add('is-opening');
      try {
        await ensureStudio(sec);
        await window.C2VStudios.open(card.dataset.c2vCard, { trigger: card, file: homeFile });
      } catch (err) {
        console.warn('[C2V accueil]', err);
        msg(T('home.studio_failed', "Le studio n'a pas pu se charger. Vérifiez votre connexion puis réessayez."), true);
      } finally {
        card.classList.remove('is-opening');
      }
    });

    // "Changer de produit" depuis le studio : retour aux cartes
    document.addEventListener('c2v:switch', e => {
      const key = e.detail && e.detail.key;
      const card = cards.find(c => c.dataset.c2vCard === key) || cards[0];
      if (!card) return;
      const target = grid || card;
      setTimeout(() => {
        target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        card.classList.add('is-hint');
        setTimeout(() => card.classList.remove('is-hint'), 1600);
      }, 120);
    });

    // Formulaire de devis (Formful) : couleur selon le produit coche
    const form = $('.c2v-home__form');
    if (form) {
      const THEMES = ['theme--base', 'theme--cyd', 'theme--ethylo'];
      const apply = () => {
        const wrap = form.querySelector('.formful__wrapper');
        if (!wrap) return;
        const sel = form.querySelector('input[name="values[MCYlLPnHRx]"]:checked');
        const v = sel ? sel.value.trim().toLowerCase() : '';
        const theme = v.includes('cyd') ? 'theme--cyd' : v.includes('ethylo') ? 'theme--ethylo' : 'theme--base';
        if (wrap.classList.contains(theme)) return;
        wrap.classList.remove(...THEMES);
        wrap.classList.add(theme);
      };
      form.addEventListener('change', e => { if (e.target && e.target.name === 'values[MCYlLPnHRx]') apply(); });
      new MutationObserver(apply).observe(form, { childList: true, subtree: true });
      apply();
    }
  }

  // Liens vers l'ancre de la section (bouton du haut de page) : defilement doux
  document.addEventListener('click', e => {
    const a = e.target.closest && e.target.closest('a[href*="#"]');
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    let url;
    try { url = new URL(a.href, location.href); } catch (err) { return; }
    if (!url.hash || url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== location.pathname.replace(/\/$/, '')) return;
    let target = null;
    try { target = document.getElementById(decodeURIComponent(url.hash.slice(1))); } catch (err) { return; }
    if (!target || !target.matches('[data-c2v-home-section]')) return;
    e.preventDefault();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    if (history.replaceState) history.replaceState(null, '', url.hash);
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  const init = () => document.querySelectorAll('[data-c2v-home-section]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
